<template>
  <div v-if="mounted" class="admin-page">
    <h1>User Permissions</h1>

    <form>
      <div v-if="errors.general" class="form-error">{{ errors.general }}</div>

      <div class="form-field">
        <label for="userId">User ID</label>
        <input id="userId" type="text" v-model="userId"/>
      </div>

      <div>
        <button type="button" @click="find">Find</button>
      </div>

      <fieldset class="form-group" v-if="showPermissionsForm">
        <legend>Roles</legend>

        <div class="checkbox-group">
          <div v-for="role in allRoles" :key="role" class="role-item">
            <label>
              <input
                  type="checkbox"
                  :value="role"
                  v-model="roles"
              />
              {{ role }}
            </label>
          </div>
        </div>
        <div v-if="errors['roles']" class="form-error">{{ errors['roles'] }}</div>
      </fieldset>

      <fieldset class="form-group" v-if="showPermissionsForm">
        <legend>Permissions</legend>

        <div class="form-field">
          <label for="groups">Groups</label>
          <textarea id="groups" v-model="groups"></textarea>
          <div v-if="errors['groups']" class="form-error">{{ errors['groups'] }}</div>
        </div>

        <div class="form-field">
          <label for="stores">Store</label>
          <textarea id="stores" v-model="stores"></textarea>
          <div v-if="errors['stores']" class="form-error">{{ errors['stores'] }}</div>
        </div>

        <div>
          <button @click="save" type="button">{{ isExisting ? "Update Permissions" : "Create Permissions" }}</button>
        </div>
      </fieldset>

    </form>

  </div>
</template>

<script setup lang="ts">
import {onMounted, ref} from "vue";
import {ValueService} from "@/services";
import {type HubRole, type Permissions} from "@/data";
import {deduplicateStringArray} from "@/utils";

const service = new ValueService();

const allRoles: HubRole[] = ["axe", "arrotex", "vidacorp", "national", "administrator"];

const userId = ref("");
const groups = ref("");
const stores = ref("");
const roles = ref<HubRole[]>();

const errors = ref<Record<string, string>>({});
const mounted = ref(false);

const showPermissionsForm = ref(false);
const isExisting = ref(false); // true if fetch returned data

onMounted(() => {
  mounted.value = true;
});

function reset() {
  userId.value = "";
  groups.value = "";
  stores.value = "";
  roles.value = [];
  errors.value = {};
  showPermissionsForm.value = false;
  isExisting.value = false;
}

const find = async () => {
  errors.value = {};
  showPermissionsForm.value = false;
  groups.value = "";
  stores.value = "";
  roles.value = [];

  if (!userId.value.trim()) {
    errors.value.userId = "User ID is required";
    return;
  }

  try {
    const result = await service.fetchUserPermissions(userId.value);

    if (result) {
      isExisting.value = true;
      groups.value = JSON.stringify(result.groups, null, 2);
      stores.value = JSON.stringify(result.stores, null, 2);
      roles.value = deduplicateStringArray(result.roles) as HubRole[];
    } else {
      // create mode: default to empty arrays
      isExisting.value = false;
      groups.value = JSON.stringify({}, null, 2);
      stores.value = JSON.stringify({}, null, 2);
      roles.value = [];
    }

    showPermissionsForm.value = true;
  } catch (error: any) {
    console.error("Failed to fetch user permissions:", error);
    errors.value.general = error.message || "An error occurred";
  }
};

const save = async () => {
  errors.value.save = "";

  try {
    const payload = {
      roles: roles.value,
      groups: JSON.parse(groups.value),
      stores: JSON.parse(stores.value),
    } as Permissions;

    await service.saveUserPermissions(userId.value, payload);
    isExisting.value = true; // after save, it is now existing
    reset();
  } catch (error: any) {
    console.error("Failed to save permissions:", error);
    errors.value.save = error.message || "An error occurred while saving";
  }
};
</script>

<style scoped src="../admin.css"></style>